import { zodResolver } from '@hookform/resolvers/zod';
import { Controller, useForm } from 'react-hook-form';
import { z } from 'zod';

import { PhoneNumberInput } from '@/components/PhoneNumberInput';
import { Button } from '@/components/ui/button';
import { CardTitle } from '@/components/ui/card';
import { Field, FieldError, FieldGroup, FieldLabel } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import { phoneNumberSchema } from '@/lib/validation-schemas';

const formId = 'reschedule-appointment-patient-identity-form';
const formSchema = z.object({
  firstName: z.string().min(1, 'First name is required'),
  lastName: z.string().min(1, 'Last name is required'),
  email: z.string().email(),
  phoneNumber: phoneNumberSchema,
});

export type RescheduleAppointmentPatientIdentityFormData = z.infer<typeof formSchema>;

export const RescheduleAppointmentPatientIdentity = ({
  onSubmit,
}: {
  onSubmit: (data: RescheduleAppointmentPatientIdentityFormData) => void;
}) => {
  const form = useForm<RescheduleAppointmentPatientIdentityFormData>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      firstName: '',
      lastName: '',
      email: '',
      phoneNumber: '',
    },
  });

  return (
    <div>
      <div className="mt-11 space-y-2">
        <p className="text-base text-charcoal-900">Reschedule</p>
        <CardTitle>First, let's find your appointment</CardTitle>
        <div className="mt-8">
          <p className="text-base text-charcoal-900">Please confirm the patient's details</p>
        </div>
      </div>
      <div className="mt-9">
        <form onSubmit={form.handleSubmit(onSubmit)}>
          <div className="space-y-8 sm:space-y-12">
            <FieldGroup>
              <div className="space-y-8">
                <Controller
                  name="firstName"
                  control={form.control}
                  render={({ field, fieldState }) => (
                    <Field data-invalid={fieldState.invalid}>
                      <FieldLabel htmlFor={`${formId}-firstName`}>Patient first name</FieldLabel>
                      <Input
                        {...field}
                        id={`${formId}-firstName`}
                        aria-invalid={fieldState.invalid}
                      />
                      {fieldState.error?.message && <FieldError errors={[fieldState.error]} />}
                    </Field>
                  )}
                />

                <Controller
                  name="lastName"
                  control={form.control}
                  render={({ field, fieldState }) => (
                    <Field data-invalid={fieldState.invalid}>
                      <FieldLabel htmlFor={`${formId}-lastName`}>Patient last name</FieldLabel>
                      <Input
                        {...field}
                        id={`${formId}-lastName`}
                        aria-invalid={fieldState.invalid}
                      />
                      {fieldState.error?.message && <FieldError errors={[fieldState.error]} />}
                    </Field>
                  )}
                />

                <Controller
                  name="email"
                  control={form.control}
                  render={({ field, fieldState }) => {
                    const inputId = `${formId}-email`;

                    return (
                      <Field data-invalid={fieldState.invalid}>
                        <FieldLabel htmlFor={inputId}>Your email</FieldLabel>
                        <Input
                          {...field}
                          id={inputId}
                          type="email"
                          aria-invalid={fieldState.invalid}
                          placeholder="name@youremail.com"
                          inputMode="email"
                        />
                        {fieldState.error?.message && <FieldError errors={[fieldState.error]} />}
                      </Field>
                    );
                  }}
                />

                <Controller
                  name="phoneNumber"
                  control={form.control}
                  render={({ field, fieldState }) => {
                    const inputId = `${formId}-phoneNumber`;

                    return (
                      <Field data-invalid={fieldState.invalid}>
                        <FieldLabel htmlFor={inputId}>Your phone number</FieldLabel>
                        <PhoneNumberInput
                          {...field}
                          id={inputId}
                          aria-invalid={fieldState.invalid}
                        />
                        {fieldState.error?.message && <FieldError errors={[fieldState.error]} />}
                      </Field>
                    );
                  }}
                />

                <Button type="submit" disabled={form.formState.isSubmitting} className="w-full">
                  {form.formState.isSubmitting ? 'Submitting...' : 'Continue'}
                </Button>
              </div>
            </FieldGroup>
          </div>
        </form>
      </div>
    </div>
  );
};
